import { NoopScrollStrategy } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import {
    FormBuilder,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthService } from 'app/auth/auth.service';
import { VerifyDialogComponent } from 'app/modules/auth/profile/verify-dialog/verify-dialog.component';
import { DashboardService } from 'app/modules/dashboard/dashboard.service';
import { RevampInputGeneralApplicationComponent } from 'app/modules/landing/corporate/revamp-input-general-application/revamp-input-general-application.component';
import { RevampSelectGeneralApplicationComponent } from 'app/modules/landing/corporate/revamp-select-general-application/revamp-select-general-application.component';
import { EncryptionService } from 'app/services/encryption.service';
import { CHANGE_LANGUAGE } from 'app/shared/utils';
import { ToastrService } from 'ngx-toastr';

@Component({
    selector: 'app-user-info-settings',
    standalone: true,
    imports: [
        CommonModule,
        TranslateModule,
        MatIconModule,
        FormsModule,
        TranslateModule,
        RouterModule,
        MatIconModule,
        ReactiveFormsModule,
        RevampInputGeneralApplicationComponent,
        RevampSelectGeneralApplicationComponent,
    ],
    templateUrl: './user-info-settings.component.html',
    styleUrl: './user-info-settings.component.scss',
})
export class UserInfoSettingsComponent implements OnInit {
    @Input() currentProfileSubTab: string;
    isRequired: any;
    pswdStrength: any;
    digitOnlyError: any;
    fullNamePatternError: any;
    isRequiredError: any;
    pswdStrengthLabel: any;
    pswdStrengthColor: any;
    invalidEmailError: any;
    phoneNoMinMaxError: any;
    fullNameMinMaxError: any;

    clickedOutside: boolean = false;
    submitted: boolean = false;
    profileForm: any;
    showVerifyMobileBtn: boolean = true;
    isMobileNoVerified: boolean = false;
    filteredPhNo: any;
    passwordForm: any;
    closeTostr: string;
    readOnlyField: boolean = false;
    previewImageUrl: string | ArrayBuffer | null = null;
    selectedFile: File | null = null;
    userData: any;

    genderType: string = '';
    genderTypeOptions = [
        {
            value: 'female',
            label: 'FORM.FEMALE',
        },
        {
            value: 'male',
            label: 'FORM.MALE',
        },
    ];
    toggleDropDown = [false];

    originalProfileData: any = {};
    isEditing = false;
    copied: boolean = false;

    constructor(
        private authService: AuthService,
        private _dashService: DashboardService,
        private formBuilder: FormBuilder,
        public translate: TranslateService,
        public dialog: MatDialog,
        private encryptionService: EncryptionService,
        private toastr: ToastrService,
        private router: Router
    ) {
        this.userData = this._dashService.getUser();
        this._dashService.getProfileImageUrl$().subscribe((url) => {
            this.previewImageUrl = url;
        });
    }

    ngOnInit() {
        this._dashService.language$.subscribe((value) => {
            this.switchLanguage(value);
        });
        this.updateTranslation();
        this.checkNumberVarified();

        this.profileForm = this.formBuilder.group({
            username: [{ value: '', disabled: true }],
            phoneNumber: [
                { value: '', disabled: true },
                [
                    Validators.required,
                    Validators.minLength(9),
                    Validators.maxLength(10),
                    Validators.pattern(/^[0-9]+$/),
                ],
            ],
            email: [{ value: '', disabled: true }],
            fullname: [
                { value: '', disabled: true },
                [
                    Validators.required,
                    Validators.minLength(2),
                    Validators.maxLength(50),
                    Validators.pattern(/^[A-Za-z\s\-']+$/),
                ],
            ],
            gender: [{ value: '', disabled: true }, [Validators.required]],
        });

        this.setFormValues(this.userData);
    }

    ngOnChanges() {
        if (!this.profileForm) return; //Don't do anything if form isn't ready
    }

    updateTranslation() {
        this.translate.get('ERROR.IS_REQUIRED').subscribe((data: any) => {
            this.isRequired = data;
            this.isRequiredError = data;
        });

        this.translate
            .get('ERROR.INVALID_EMAIL_ENTERED')
            .subscribe((data: any) => {
                this.invalidEmailError = data;
            });

        this.translate.get('ERROR.DIGIT_ONLY').subscribe((data: any) => {
            this.digitOnlyError = data;
        });

        this.translate.get('ERROR.FULL_NAME_PATTERN').subscribe((data: any) => {
            this.fullNamePatternError = data;
        });

        this.translate.get('ERROR.PHONE_NO').subscribe((data: any) => {
            this.phoneNoMinMaxError = data;
        });

        this.translate.get('ERROR.FULL_NAME').subscribe((data: any) => {
            this.fullNameMinMaxError = data;
        });

        this.translate.get('COMMON.CLOSE').subscribe((data: any) => {
            this.closeTostr = data;
        });
    }

    // this function is used to check if the mobile number is verified or not and sets the mobile number buttons accordingly
    checkNumberVarified() {
        const encryptionKey = atob(localStorage.getItem('cepn'));
        const encryptedProfile = this.encryptionService.decryptData(
            localStorage.getItem('profile'),
            encryptionKey
        );
        let encodedProfile = encryptedProfile ? atob(encryptedProfile) : null;
        let profile = encodedProfile
            ? JSON.parse(encodedProfile)
            : { username: '', phoneNumber: '', email: '' };
        if (profile.smsverify == 'Unverified') {
            this.showVerifyMobileBtn = true;
            this.isMobileNoVerified = true;
        } else if (profile.smsverify == 'Verified') {
            this.showVerifyMobileBtn = false;
            this.isMobileNoVerified = false;
        }
    }

    get fProfile() {
        return this?.profileForm?.controls;
    }

    onImageSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files[0]) {
            const file = input.files[0];
            this.selectedFile = file;

            const reader = new FileReader();
            reader.onload = () => {
                this.previewImageUrl = reader.result;
            };
            reader.readAsDataURL(file);

            // Call upload function
            this.uploadProfileImage(file);
        }
    }

    uploadProfileImage(file: File): void {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('alias', 'PROFILE');
        const headers = {
            pooltype: 'user',
        };
        this._dashService.uploadProfileImage(formData, headers).subscribe({
            next: (response) => {
                // Refresh the image URL across the app
                this._dashService.fetchProfileImageData();
            },
            error: (error) => {
                console.error('Upload error:', error);
            },
        });
    }

    verifyMobile(item) {
        let data = {};
        this.authService.generateSmsOTP(data).subscribe({
            next: (response) => {
                const dialogRef = this.dialog.open(VerifyDialogComponent, {
                    hasBackdrop: true,
                    panelClass: 'verify-mobile-dialog',
                    backdropClass: 'verify-mobile-background',
                    scrollStrategy: new NoopScrollStrategy(),
                    data: {
                        clientTranKey: JSON.parse(response).client_Trans_Id,
                        referenceid: JSON.parse(response).reference_Id,
                    },
                });

                dialogRef.afterClosed().subscribe((result) => {
                    if (result) {
                        this.toastr.success('Verification is successful.');
                        const encryptionKey = atob(
                            localStorage.getItem('cepn')
                        );
                        let data = JSON.parse(
                            atob(
                                this.encryptionService.decryptData(
                                    localStorage.getItem('profile'),
                                    encryptionKey
                                )
                            )
                        );
                        data.smsverify = 'Verified';
                        if (data.smsverify == 'Verified') {
                            this.showVerifyMobileBtn = false;
                            this.isMobileNoVerified = false;
                        } else if (data.smsverify == 'Unverified') {
                            this.showVerifyMobileBtn = true;
                            this.isMobileNoVerified = true;
                        }
                        localStorage.setItem(
                            'profile',
                            this.encryptionService.encryptData(
                                btoa(JSON.stringify(data)),
                                encryptionKey
                            )
                        );
                    }
                });
            },
            error: (err) => {
                this.toastr.error('something went wrong please retry');
            },
        });
    }

    getErrorsProfile(input: any, inputName?: any): Array<any> {
        let errors = [];
        const lengthConstraints: {
            [key: string]: { min: number; max: number; errorMsg: string };
        } = {
            fullname: { min: 2, max: 50, errorMsg: this.fullNameMinMaxError },
            phoneNumber: {
                min: 10,
                max: 15,
                errorMsg: this.phoneNoMinMaxError,
            },
        };

        if (this.submitted) {
            const control = this.fProfile[input];

            if (control.errors?.required) {
                errors.push(`${inputName} ${this.isRequired}`);
            }

            if (
                (this.fProfile[input].errors?.minlength ||
                    this.fProfile[input].errors?.maxlength) &&
                lengthConstraints[input]
            ) {
                errors.push(
                    `${inputName} ${lengthConstraints[input].errorMsg}`
                );
            }

            if (input === 'phoneNumber' && control.errors?.pattern) {
                errors.push(`${inputName} ${this.digitOnlyError}`);
            }

            if (input === 'fullname' && control.errors?.pattern) {
                errors.push(`${inputName} ${this.fullNamePatternError}`);
            }
        }

        return errors;
    }

    getLengthError(input: string): string {
        const constraints: any = {
            fullname: this.fullNameMinMaxError,
            phoneNumber: this.phoneNoMinMaxError,
        };
        return constraints[input] || '';
    }

    handleGenderTypeOptions(value: any) {
        this.genderType = value;
        this.profileForm.patchValue({
            gender: this.genderType,
        });
        this.profileForm.patchValue({ gender: value });
    }

    setFormValues(data: any) {
        let filteredPhNo = this.userData?.phoneNumber.replace('+60', '');
        if (data?.details?.gender != null && data.details.gender !== '') {
            this.handleGenderTypeOptions(data?.details?.gender);
        }
        this.profileForm.patchValue({
            username: data?.username || '',
            email: data?.email || '',
            gender: data?.details?.gender || '',
            fullname: data?.details?.fullname || '',
            phoneNumber: filteredPhNo || '',
        });
    }

    closeDropdown() {
        for (let i = 0; i < 4; i++) {
            this.toggleDropDown[i] = false;
        }
    }

    onEditProfile(): void {
        this.isEditing = true;
        if (this.isMobileNoVerified) {
            this.showVerifyMobileBtn = true;
        }
        this.originalProfileData = this.profileForm.getRawValue();

        this.profileForm.get('fullname')?.enable();
        this.profileForm.get('gender')?.enable();
        this.profileForm.get('phoneNumber')?.enable();
    }

    onCancelProfileDetails() {
        this.isEditing = false;
        if (this.isMobileNoVerified) {
            this.showVerifyMobileBtn = true;
        }
        // Restore original data
        this.profileForm.patchValue(this.originalProfileData);
        // Re-disable editable fields
        this.profileForm.get('fullname')?.disable();
        this.profileForm.get('gender')?.disable();
        this.profileForm.get('phoneNumber')?.disable();
    }

    hasValue(value: any): boolean {
        return value != null && value !== '';
    }

    onProfileFormSubmit(data) {
        this.closeDropdown();
        this.submitted = true;
        if (this.profileForm.invalid) {
            return;
        } else {
            let profileObj: any = {
                username: this.userData.username,
                email: this.userData.email,
                ...(this.hasValue(this.fProfile['phoneNumber']?.value) && {
                    phoneNumber: '+60' + this.fProfile['phoneNumber'].value,
                }),
                details: {
                    ...(this.hasValue(data.gender) && { gender: data.gender }),
                    ...(this.hasValue(data.fullname) && {
                        fullname: data.fullname,
                    }),
                },
            };
            if (Object.keys(profileObj.details).length === 0) {
                delete profileObj.details;
            }

            this.authService.updateProfile(profileObj).subscribe({
                next: (res) => {
                    if (localStorage.getItem('language') == 'bm') {
                        this.toastr.success(
                            'Profil anda telah berjaya dikemas kini'
                        );
                    } else {
                        this.toastr.success(
                            'Your profile has been successfully updated'
                        );
                    }
                    this.isEditing = false;
                    // Re-disable editable fields
                    this.profileForm.get('fullname')?.disable();
                    this.profileForm.get('gender')?.disable();
                    this.profileForm.get('phoneNumber')?.disable();
                    const encryptionKey = atob(localStorage.getItem('cepn'));
                    const profileData = atob(
                        this.encryptionService.decryptData(
                            localStorage.getItem('profile'),
                            encryptionKey
                        )
                    );
                    let profile = JSON.parse(profileData);
                    profile.phoneNumber = this.fProfile['phoneNumber'].value;
                    profile.smsverify = res.smsverify;
                    profile.details.fullname = res.details.fullname;
                    profile.details.gender = res.details.gender;
                    if (profile.smsverify == 'Verified') {
                        this.showVerifyMobileBtn = false;
                        this.isMobileNoVerified = false;
                    } else if (profile.smsverify == 'Unverified') {
                        this.showVerifyMobileBtn = true;
                        this.isMobileNoVerified = true;
                    }
                    localStorage.setItem(
                        'profile',
                        this.encryptionService.encryptData(
                            btoa(JSON.stringify(profile)),
                            encryptionKey
                        )
                    );
                    this.fProfile['phoneNumber'].disable();
                },
                error: (err) => {
                    let error = JSON.parse(err.error);
                    this.toastr.error(error.message);
                },
            });
        }
    }

    switchLanguage(language: string) {
        CHANGE_LANGUAGE(language, this.router.url, this.translate);
        this.updateTranslation();
    }

    copyReferralCode() {
        const code = location.origin + '/sign-up?ref=' + this.userData.username;
        navigator.clipboard.writeText(code).then(() => {
            this.copied = true;
            setTimeout(() => {
                this.copied = false;
            }, 3000);
        });
    }
}
